<template>
  <div class="c-dropdown">
    <slot name="button" v-bind="{ toggle: onToggle, identifier: slotIdentifier }"/>

    <transition name="fade-transition" mode="out-in" @after-leave="onDestroy">
      <div v-show="isActive" :class="['c-dropdown__menu', { 'c-dropdown__menu--is-mobile-fullscreen': isMobileFullscreen }]" :style="menuStyle" ref="dropdownMenu" v-bind="$attrs">
        <div class="c-dropdown__menu-wrapper">
          <div class="c-dropdown__menu-header" v-if="headerTitle && isMobileFullscreen">
            <div class="c-dropdown__menu-header-title">
              {{ headerTitle }}
            </div>
            <base-button v-if="canShowClose" class="c-dropdown__menu-header-close" variant="link" :icon-append="['fal', 'times']" @click="onClose('x')" />
          </div>
          <div class="c-dropdown__menu-body" ref="content" :style="menuBodyStyle" tabindex="-1">
            <slot />
          </div>
          <div class="c-dropdown__menu-footer" v-if="hasSlots('footer')">
            <slot name="footer" v-bind="{ ok: onOk, cancel: onCancel, hide: onHide, close: onClose }" />
          </div>
        </div>

        <div class="c-dropdown__arrow" data-popper-arrow />
      </div>
    </transition>
  </div>
</template>

<script>
import { createPopper } from '@popperjs/core'
import { event, shortcut } from '@/mixins/helpers'

const placements = ['auto', 'auto-start', 'auto-end', 'top', 'top-start', 'top-end', 'bottom', 'bottom-start', 'bottom-end', 'right', 'right-start', 'right-end', 'left', 'left-start', 'left-end']

const defaultCloseOptions = ['escape', 'outside', 'close', 'x']

export default {
  name: 'BaseDropdown',

  inheritAttrs: false,

  props: {
    value: {
      type: Boolean,
      defautl: false
    },
    buttonTitle: {
      type: String
    },
    headerTitle: {
      type: String
    },
    canClose: {
      type: [Array, Boolean],
      default: true
    },
    minWidth: {
      type: [String, Number],
      default: 300
    },
    maxWidth: {
      type: [String, Number],
      default: null
    },
    maxHeight: {
      type: [String, Number],
      required: false
    },
    placement: {
      type: String,
      default: 'bottom-start',
      validator: value => placements.includes(value)
    },
    appendToBody: {
      type: Boolean,
      default: true
    },
    isMobileFullscreen: {
      type: Boolean,
      default: false
    }
  },

  mixins: [ event, shortcut ],

  data () {
    return {
      localValue: this.value,
      slotIdentifier: Math.random().toString(36).substring(2, 10),

      isAppendedToBody: false,

      popperInstance: null,
      dropdownMenu: null
    }
  },

  mounted () {
    this.dropdownMenu = this.$el.querySelector('.c-dropdown__menu')

    this.$watch(() => this.value, value => {
      if (this.localValue !== value) {
        value ? this.show() : this.hide()
      }
    }, { immediate: true })
  },

  beforeDestroy () {
    if (this.isActive) {
      this.hide()

      this.$nextTick(() => {
        this.onDestroy()
      })
    }
  },

  computed: {
    isActive: {
      get () {
        return this.localValue
      },
      set (value) {
        this.localValue = value
        this.$emit('input', value)
      }
    },

    closeOptions () {
      return typeof this.canClose === 'boolean' ? this.canClose ? defaultCloseOptions : [] : this.canClose
    },

    canShowClose () {
      return this.closeOptions.includes('x')
    },

    menuStyle () {
      return {
        minWidth: this.minWidth + 'px',
        maxWidth: this.maxWidth + 'px'
      }
    },

    menuBodyStyle () {
      return {
        maxHeight: this.maxHeight + 'px'
      }
    }
  },

  methods: {
    show () {
      if (this.popperInstance) {
        this.popperInstance.destroy()
      }

      if (this.appendToBody && !this.isAppendedToBody) {
        this.isAppendedToBody = true
        document.body.appendChild(this.dropdownMenu)
      }

      if (this.isMobileFullscreen) {
        document.body.classList.add('is-noscroll--sm-down')
      }

      this.isActive = true

      this.$eventHub.emit('dropdown.open')

      this.$nextTick(() => {
        this.popperInstance = createPopper(this.$el.querySelector(`[id="${this.slotIdentifier}"]`), this.dropdownMenu, {
          placement: this.placement,
          modifiers: [
            {
              name: 'offset',
              options: {
                offset: [0, 8]
              }
            }],
          onFirstUpdate: () => {
            this.$nextTick(() => {
              this.popperInstance.update()

              setTimeout(() => {
                this.$refs.content.focus()
                this.setListeners(true)
              }, 1)
            })
          }
        })
      })
    },

    hide (trigger, payload) {
      const event = { preventDefault: function () { this.defaultPrevented = true }, defaultPrevented: false, ...payload }

      if (trigger) {
        this.$emit(trigger, event)
      }

      if (this.isActive && event.defaultPrevented === false) {
        this.isActive = false

        this.$nextTick(() => {
          this.setListeners(false)

          if (this.isMobileFullscreen) {
            document.body.classList.remove('is-noscroll--sm-down')
          }

          this.$emit('hidden', arguments)
          this.$emit('input', false)
        })
      }
    },

    onOk (payload) {
      this.hide('ok', payload)
    },

    onCancel (payload) {
      this.hide('cancel', payload)
    },

    onHide (payload) {
      this.hide('hide', payload)
    },

    onClose (method = 'close', payload) {
      if (this.closeOptions.includes(method)) {
        this.hide('close', { method, payload })
      }
    },

    onDocumentClick (event) {
      if (this.isActive && this.isInWhiteList(event.target) === false) {
        this.onClose('outside')
      }
    },

    onEvent () {
      if (this.isActive) {
        this.hide()
      }
    },

    onToggle () {
      this.isActive ? this.hide() : this.show()
    },

    onDestroy () {
      if (this.popperInstance) {
        this.popperInstance.destroy()
        this.popperInstance = null
      }

      if (this.isAppendedToBody) {
        this.isAppendedToBody = false
        document.body.removeChild(this.dropdownMenu)
      }
    },

    setListeners (isActive) {
      const events = [{ name: 'click', handler: this.onDocumentClick }]
      events.forEach(event => isActive ? this.addEventListener(event) : this.removeEventListener(event))

      const shortcuts = [{ combinations: 'enter', handler: this.onOk }, { combinations: 'esc', handler: () => this.onClose('escape') }]
      shortcuts.forEach(shortcut => isActive ? this.bind(shortcut) : this.unbind(shortcut))

      isActive ? this.$eventHub.on('dropdown.open', this.onEvent) : this.$eventHub.off('dropdown.open', this.onEvent)
    },

    isInWhiteList (element) {
      return [this.$refs.dropdownMenu, ...this.$refs.dropdownMenu.querySelectorAll('*')].includes(element)
    },

    hasSlots (name) {
      return this.$scopedSlots[name] && (this.$scopedSlots[name]() || []).some(item => !item.isComment)
    }
  }
}
</script>
